import { Eye, QrCode } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../../context/AppStateContext';
import { Badge } from '../common/Badge';
import { PageHeader } from '../common/PageHeader';
import { ui } from '../../styles/ui';
import { getVerificationDisplayStatus, VERIFICATION_STATUS_ICON, VERIFICATION_STATUS_LABEL, VERIFICATION_STATUS_TONE } from '../../utils/verificationStatus';

export const ScanHistory = () => {
  const navigate = useNavigate();
  const { scanHistory, loadScanHistory } = useAppState();
  const [searchTerm, setSearchTerm] = useState('');

  // Refetch every time this tab is opened, not just once at login.
  useEffect(() => {
    loadScanHistory(undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredScans = useMemo(() => {
    return scanHistory.filter((product) => {
      return searchTerm === '' ||
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.barcode?.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [scanHistory, searchTerm]);

  const openDetails = (product) => navigate(`/admin/products/${product._id}`);

  return (
    <div className="space-y-4">
      <PageHeader title="Scan History" subtitle="Track all product verification scans" />
      <input
        type="text"
        placeholder="Search scans..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className={`${ui.input} sm:max-w-sm`}
      />

      <div className={`${ui.card} overflow-hidden`}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing <span className="font-semibold text-gray-900 dark:text-white">{filteredScans.length}</span> scan records
          </div>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className={ui.tableHeadCell}>Product</th>
                <th className={ui.tableHeadCell}>Barcode</th>
                <th className={ui.tableHeadCell}>Category</th>
                <th className={ui.tableHeadCell}>Status</th>
                <th className={ui.tableHeadCell}>Scan Date</th>
                <th className={`${ui.tableHeadCell} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {filteredScans.map((product) => {
                const status = getVerificationDisplayStatus(product);
                const StatusIcon = VERIFICATION_STATUS_ICON[status] || VERIFICATION_STATUS_ICON.default;
                return (
                  <tr key={product._id} className={ui.tableRow}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-9 h-9 bg-orange-600 rounded-lg flex items-center justify-center mr-3 shrink-0">
                          <QrCode className="h-[18px] w-[18px] text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">{product.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">ID: {product._id?.slice(-8)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono font-semibold">{product.barcode}</code>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge tone="info">{product.category}</Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge tone={VERIFICATION_STATUS_TONE[status]} icon={StatusIcon} spin={status === 'in_progress'}>
                        {VERIFICATION_STATUS_LABEL[status]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-300">{new Date(product.createdAt).toLocaleDateString()}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{new Date(product.createdAt).toLocaleTimeString()}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button onClick={() => openDetails(product)} className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors" title="View Scan Details">
                        <Eye className="h-[18px] w-[18px]" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700/60">
          {filteredScans.map((product) => {
            const status = getVerificationDisplayStatus(product);
            const StatusIcon = VERIFICATION_STATUS_ICON[status] || VERIFICATION_STATUS_ICON.default;
            return (
              <div key={product._id} className="p-3.5 flex items-center justify-between">
                <div className="flex items-center min-w-0">
                  <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center mr-2.5 shrink-0">
                    <QrCode className="h-4 w-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{product.name}</div>
                    <Badge tone={VERIFICATION_STATUS_TONE[status]} icon={StatusIcon} spin={status === 'in_progress'} className="mt-1">
                      {VERIFICATION_STATUS_LABEL[status]}
                    </Badge>
                  </div>
                </div>
                <button onClick={() => openDetails(product)} className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
                  <Eye className="h-[18px] w-[18px]" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};
